import React, { useState, useEffect } from 'react';
import DOMPurify from "dompurify";

// Material UI
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';

// Material UI Icons
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

// Componentes 
import SeleccionarRubro from './SeleccionarRubro';
import SeleccionarEspecialidades from './SeleccionarEspecialidades';
import SeleccionarPais from './SeleccionarPais';
import SeleccionarProvincia from './SeleccionarProvincia';
import SeleccionarLocalidad from './SeleccionarLocalidad';
import SeleccionarPlanes from './SeleccionarPlanes';

// Funciones para obtener datos
import ObtenerPrestador from '../functions/ObtenerPrestador';
import ObtenerProvincia from '../functions/ObtenerProvincia';
import ObtenerLocalidad from '../functions/ObtenerLocalidad';
import GuardarPrestador from '../functions/GuardarPrestador';
import InsertarPrestador from '../functions/InsertarPrestador';
import ObtenerRubros from '../functions/ObtenerRubros';
import ObtenerPrestadorEspecialidades from '../functions/ObtenerPrestadorEspecialidades';
import GuardarEspecialidadesPrestador from '../functions/GuardarEspecialidadesPrestador';
import GuardarPlanesPrestador from '../functions/GuardarPlanesPrestador';

// Tipos de datos
import PrestadorTabla from '../types/PrestadorTabla';
import PrestadorSeleccionar from '../types/PrestadorSeleccionar';
import EspecialidadSeleccionar from '../types/EspecialidadSeleccionar';
import PaisSeleccionar from '../types/PaisSeleccionar';
import ProvinciaSeleccionar from '../types/ProvinciaSeleccionar';
import LocalidadSeleccionar from '../types/LocalidadSeleccionar';
import PlanSeleccionar from '../types/PlanSeleccionar';

// Quill components
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Estilo para el editor Quill

// Indicador de procesamiento
import CircularProgress from '@mui/material/CircularProgress';
import RubroSeleccionar from '../types/RubroSeleccionar';

interface EditarPrestadorProps {
    idPrestador: number;
    guardarCambios: (prestador: PrestadorTabla) => void;
    cancelarCambios: () => void;
    eliminando: boolean;
}

export const EditarPrestador: React.FC<EditarPrestadorProps> = ({ idPrestador, guardarCambios, cancelarCambios, eliminando = false }) => {
  
    const [isLoading, setIsLoading] = useState(true);
    const [prestador, setPrestador] = useState<PrestadorTabla | null>(null);
    const [prestadorEspecialidades, setPrestadorEspecialidades] = useState<PrestadorSeleccionar>({id: 0, prestador:'', especialidades: [], planes: []});

    const [errorPrestador, setErrorPrestador] = useState<string | null>(null);

    const [localCuit, setLocalCuit] = useState('');
    const [localRazonSocial, setLocalRazonSocial] = useState('');
    const [localNombreComercial, setLocalNombreComercial] = useState('');
    const [localDomicilio, setLocalDomicilio] = useState('');
    const [localTelefono, setLocalTelefono] = useState('');
    const [localWhatsapp, setLocalWhatsapp] = useState('');
    const [localEmail, setLocalEmail] = useState('');
    const [localSitioweb, setLocalSitioweb] = useState('');
    const [localInstagram, setLocalInstagram] = useState('');
    const [localFacebook, setLocalFacebook] = useState('');
    const [localYoutube, setLocalYoutube] = useState('');
    const [localTiktok, setLocalTiktok] = useState('');
    const [localLinkedin, setLocalLinkedin] = useState('');
    const [localGpslat, setLocalGpslat] = useState('');
    const [localGpslon, setLocalGpslon] = useState('');
    const [localIdrubro, setLocalIdrubro] = useState(0);
    const [localRubro, setLocalRubro] = useState('');
    const [localHabilitado, setLocalHabilitado] = useState('');
    const [localFchCrea, setLocalFchCrea] = useState('');
    const [localFchModi, setLocalFchModi] = useState('');
    const [localDadoBaja, setLocalDadoBaja] = useState('');
    const [localFchBaja, setLocalFchBaja] = useState('');
    const [localIdPais, setLocalIdPais] = useState(0);
    const [localPais, setLocalPais] = useState('');
    const [localIdProvincia, setLocalIdProvincia] = useState(0);
    const [localProvincia, setLocalProvincia] = useState('');  
    const [localIdLocalidad, setLocalIdLocalidad] = useState(0);
    const [localLocalidad, setLocalLocalidad] = useState('');
    const [localCodPostal, setLocalCodPostal] = useState('');
    const [localLogobig, setLocalLogobig] = useState('');
    const [localLogosmall, setLocalLogosmall] = useState('');
    const [localObservaciones, setLocalObservaciones] = useState('');


    // Método para ordenar las especialidades del prestador antes de guardarlas en el arreglo 
    const ordenarEspecialidades = (especialidades: EspecialidadSeleccionar[]) => {
        return especialidades.sort((a, b) => {
            if (a.especialidad < b.especialidad) {
                return -1;
            }
            if (a.especialidad > b.especialidad) {
                return 1;
            }
            return 0;
        });
    };

    // Método para ordenar los planes del prestador antes de guardarlas en el arreglo 
    const ordenarPlanes = (planes: PlanSeleccionar[]) => {
        return planes.sort((a, b) => {
            if (a.title < b.title) {
                return -1;
            }
            if (a.title > b.title) {
                return 1;
            }
            return 0;
        });
    };

    const handleSeleccionarRubro = (rubroSeleccionado: RubroSeleccionar | null) => {
        setLocalIdrubro(rubroSeleccionado?.id || 0);
    };

    const handleClearSeleccionarRubro = () => {
        setLocalIdrubro(0);
    }

    const handlePaisChange = (paisSeleccionado: PaisSeleccionar) => {
        setLocalIdPais(paisSeleccionado.id);
    }

    const handlePaisClear = () => {
        setLocalIdPais(0);
    }

    const handleProvinciaChange = (provinciaSeleccionada: ProvinciaSeleccionar) => {
        setLocalIdProvincia(provinciaSeleccionada.id);
    }

    const handleProvinciaClear = () => {
        setLocalIdProvincia(0);
    }

    const handleLocalidadChange = (localidadSeleccionada: LocalidadSeleccionar) => {
        setLocalIdLocalidad(localidadSeleccionada.id);
    }

    const handleLocalidadClear = () => {
        setLocalIdLocalidad(0);
    }


    useEffect(() => {
        
        const fetchPrestador = async () => {
            try {

                const data = await ObtenerPrestador(idPrestador);

                if (data.prestador_ok === 'si') {

                    // Todo ok, prestador recuperado
                    const prestadorRecuperado: PrestadorTabla = {
                        id: data.prestador[0].id,
                        cuit: data.prestador[0].cuit,
                        razonsocial: data.prestador[0].razonsocial,
                        nombrecomercial: data.prestador[0].nombrecomercial,
                        domicilio: data.prestador[0].domicilio,
                        telefono: data.prestador[0].telefono,
                        whatsapp: data.prestador[0].whatsapp,
                        email: data.prestador[0].email,
                        sitioweb: data.prestador[0].sitioweb,
                        instagram: data.prestador[0].instagram,
                        facebook: data.prestador[0].facebook,
                        youtube: data.prestador[0].youtube,
                        tiktok: data.prestador[0].tiktok,
                        linkedin: data.prestador[0].linkedin,
                        gpslat: data.prestador[0].gpslat,
                        gpslon: data.prestador[0].gpslon,
                        idrubro: data.prestador[0].idrubro,
                        rubro: data.prestador[0].rubro,
                        habilitado: data.prestador[0].habilitado,
                        fecha_creacion: data.prestador[0].fecha_creacion,
                        fecha_modificacion: data.prestador[0].fecha_modificacion,
                        dado_de_baja: data.prestador[0].dado_de_baja,
                        fecha_baja: data.prestador[0].fecha_baja,
                        idpais: data.prestador[0].idpais,
                        pais: data.prestador[0].pais,
                        idprovincia: data.prestador[0].idprovincia,
                        provincia: data.prestador[0].provincia,
                        idlocalidad: data.prestador[0].idlocalidad,
                        localidad: data.prestador[0].localidad,
                        codpostal: data.prestador[0].codpostal,
                        logo_big: data.prestador[0].logo_big,
                        logo_small: data.prestador[0].logo_small,
                        observaciones: data.prestador[0].observaciones
                    };

                    setPrestador(prestadorRecuperado);

                    setLocalCuit(data.prestador[0].cuit);
                    setLocalRazonSocial(data.prestador[0].razonsocial);
                    setLocalNombreComercial(data.prestador[0].nombrecomercial);
                    setLocalDomicilio(data.prestador[0].domicilio);
                    setLocalTelefono(data.prestador[0].telefono);
                    setLocalWhatsapp(data.prestador[0].whatsapp);
                    setLocalEmail(data.prestador[0].email);
                    setLocalSitioweb(data.prestador[0].sitioweb);
                    setLocalInstagram(data.prestador[0].instagram);
                    setLocalFacebook(data.prestador[0].facebook);
                    setLocalYoutube(data.prestador[0].youtube);
                    setLocalTiktok(data.prestador[0].tiktok);
                    setLocalLinkedin(data.prestador[0].linkedin);
                    setLocalGpslat(data.prestador[0].gpslat);
                    setLocalGpslon(data.prestador[0].gpslon);
                    setLocalIdrubro(data.prestador[0].idrubro);
                    setLocalRubro(data.prestador[0].rubro);
                    setLocalHabilitado(data.prestador[0].habilitado);
                    setLocalFchCrea(data.prestador[0].fecha_creacion);
                    setLocalFchModi(data.prestador[0].fecha_modificacion);
                    setLocalDadoBaja(data.prestador[0].dado_de_baja);
                    setLocalFchBaja(data.prestador[0].fecha_baja);
                    setLocalIdPais(data.prestador[0].idpais);
                    setLocalPais(data.prestador[0].pais);
                    setLocalIdProvincia(data.prestador[0].idprovincia);
                    setLocalProvincia(data.prestador[0].provincia);
                    setLocalIdLocalidad(data.prestador[0].idlocalidad);
                    setLocalLocalidad(data.prestador[0].localidad);
                    setLocalCodPostal(data.prestador[0].codpostal);
                    setLocalLogobig(data.prestador[0].logo_big);
                    setLocalLogosmall(data.prestador[0].logo_small);
                    setLocalObservaciones(data.prestador[0].observaciones);

                    const dataPrestadorEspecialidades = await ObtenerPrestadorEspecialidades(idPrestador);

                    if (dataPrestadorEspecialidades.id > 0) {
                        setPrestadorEspecialidades(dataPrestadorEspecialidades);
                    }

                    setErrorPrestador(null);

                } else {
                    
                    // Hubo errores al recuperar el pais
                    setErrorPrestador(data.mensaje);
                    setPrestador(null);
                    setLocalCuit('');
                    setLocalRazonSocial('');
                    setLocalNombreComercial('');
                    setLocalDomicilio('');
                    setLocalTelefono('');
                    setLocalWhatsapp('');
                    setLocalEmail('');
                    setLocalSitioweb('');
                    setLocalInstagram('');
                    setLocalFacebook('');
                    setLocalYoutube('');
                    setLocalTiktok('');
                    setLocalLinkedin('');
                    setLocalGpslat('');
                    setLocalGpslon('');
                    setLocalIdrubro(0);
                    setLocalRubro('');
                    setLocalHabilitado('');
                    setLocalFchCrea('');
                    setLocalFchModi('');
                    setLocalFchBaja('');
                    setLocalDadoBaja('');
                    setLocalIdPais(0);
                    setLocalPais('');
                    setLocalIdProvincia(0);
                    setLocalProvincia('');
                    setLocalIdLocalidad(0);
                    setLocalLocalidad('');
                    setLocalCodPostal('');
                    setLocalLogobig('');
                    setLocalLogosmall('');
                    setLocalObservaciones('');
                }

                setIsLoading(false);
                                
            } catch (err) {
                setErrorPrestador('Error al obtener la información del pais.');
                setPrestador(null);
                setLocalCuit('');
                setLocalRazonSocial('');
                setLocalNombreComercial('');
                setLocalDomicilio('');
                setLocalTelefono('');
                setLocalWhatsapp('');
                setLocalEmail('');
                setLocalSitioweb('');
                setLocalInstagram('');
                setLocalFacebook('');
                setLocalYoutube('');
                setLocalTiktok('');
                setLocalLinkedin('');
                setLocalGpslat('');
                setLocalGpslon('');
                setLocalIdrubro(0);
                setLocalRubro('');
                setLocalHabilitado('');
                setLocalFchCrea('');
                setLocalFchModi('');
                setLocalFchBaja('');
                setLocalDadoBaja('');
                setLocalIdPais(0);
                setLocalPais('');
                setLocalIdProvincia(0);
                setLocalProvincia('');
                setLocalIdLocalidad(0);
                setLocalLocalidad('');
                setLocalCodPostal('');
                setLocalLogobig('');
                setLocalLogosmall('');
                setLocalObservaciones('');
                setIsLoading(false);
            }
        };

        if (idPrestador > 0) {
            // Estoy editando un prestador
            fetchPrestador();
        } else {
            // Estoy creando un nuevo prestador
            setIsLoading(false);
            setPrestador({id:0, cuit: '', razonsocial: '', nombrecomercial: '', domicilio: '', telefono: '', whatsapp: '', email: '', sitioweb: '', instagram: '', facebook: '', youtube: '', tiktok: '', linkedin: '', gpslat: '', gpslon: '', idrubro: 0, rubro: '', habilitado: '', fecha_creacion: '', fecha_modificacion: '', dado_de_baja: '', fecha_baja: '', idpais: 0, pais: '', idprovincia: 0, provincia: '', idlocalidad: 0, localidad: '', codpostal: '', logo_big: '', logo_small: '', observaciones: ''});
            setLocalHabilitado('S');
            setLocalDadoBaja('N');
        }

    }, []);

    const handleChangeLocalCuit = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^[0-9]{0,11}$/;
        if (pattern.test(event.target.value)) {
            setLocalCuit(event.target.value);
        }
    };

    const handleChangeLocalRazonSocial = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalRazonSocial(event.target.value.toUpperCase());
    };

    const handleChangeLocalNombreComercial = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalNombreComercial(event.target.value.toUpperCase());
    };

    const handleChangeLocalDomicilio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalDomicilio(event.target.value.toUpperCase());
    };

    const handleChangeLocalTelefono = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^[;0-9]{0,50}$/;
        if (pattern.test(event.target.value)) {
            setLocalTelefono(event.target.value);
        }
    };

    const handleChangeLocalWhatsapp = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^[0-9]{0,10}$/;
        if (pattern.test(event.target.value)) {
            setLocalWhatsapp(event.target.value);
        }
    };

    const handleChangeLocalEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalEmail(event.target.value.toUpperCase());
    };

    const handleChangeLocalSitioweb = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalSitioweb(event.target.value);
    };

    const handleChangeLocalInstagram = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalInstagram(event.target.value);
    };

    const handleChangeLocalFacebook = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalFacebook(event.target.value);
    };

    const handleChangeLocalYoutube = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalYoutube(event.target.value);
    };

    const handleChangeLocalTiktok = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalTiktok(event.target.value);
    };

    const handleChangeLocalLinkedin = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalLinkedin(event.target.value);
    };

    const handleChangeLocalGpslat = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^[-.0-9]{0,30}$/;
        if (pattern.test(event.target.value)) {
            setLocalGpslat(event.target.value);
        }
    };

    const handleChangeLocalGpslon = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^[-.0-9]{0,30}$/;
        if (pattern.test(event.target.value)) {
            setLocalGpslon(event.target.value);
        }
    };

    const handleChangeLocalIdrubro = (event: SelectChangeEvent) => {
        let valor = parseInt(event.target.value);
        setLocalIdrubro(valor);
    };


    const handleChangeLocalHabitado = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalHabilitado(valorMayuscula);
    };

    const handleChangeLocalDadoBaja = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalDadoBaja(valorMayuscula);
    };

    // Para actualizar las especialidades del prestador (ante cambios)
    const handleEspecialidadesChange = (newEspecialidades: EspecialidadSeleccionar) => {
        
        // Hago una copia de las especialidades actuales
        const especialidadesCopy = [...prestadorEspecialidades.especialidades];

        // Agrego la nueva especialidad
        especialidadesCopy.push(newEspecialidades);

        // Ahora ordeno las especialidades
        const especialidadesOrdenadas = ordenarEspecialidades(especialidadesCopy);
        
        setPrestadorEspecialidades({...prestadorEspecialidades, especialidades: especialidadesOrdenadas});
    };

    
    // Para quitar una especialidad del prestador
    const handleEspecialidadesRemove = (especialidad: EspecialidadSeleccionar) => {
        // Filtro las especialidades para quitar la especialidad que se desea eliminar
        const especialidadesCopy = prestadorEspecialidades.especialidades.filter((item) => item.id !== especialidad.id);
        
        // Actualizo el estado de las especialidades
        setPrestadorEspecialidades({...prestadorEspecialidades, especialidades: especialidadesCopy});
    }

    // Para limpiar todas las especialidades del prestador
    const handleEspecialidadesClear = () => {
        
        // Mantengo los datos del prestador pero sin especialidades
        setPrestadorEspecialidades({...prestadorEspecialidades, especialidades: []});
    
    }


    // Para actualizar los planes del prestador (ante cambios)
    const handlePlanesChange = (newPlanes: PlanSeleccionar) => {
        
        // Hago una copia de los planes actuales
        const planesCopy = [...prestadorEspecialidades.planes];

        // Agrego el nuevo plan
        planesCopy.push(newPlanes);

        // Ahora ordeno las especialidades
        const planesOrdenados = ordenarPlanes(planesCopy);
        
        setPrestadorEspecialidades({...prestadorEspecialidades, planes: planesOrdenados});
    };

    // Para quitar una especialidad del prestador
    const handlePlanesRemove = (plan: PlanSeleccionar) => {
        // Filtro los planes para quitar el plan que se desea eliminar
        const planesCopy = prestadorEspecialidades.planes.filter((item) => item.id !== plan.id);
        
        // Actualizo el estado de los planes
        setPrestadorEspecialidades({...prestadorEspecialidades, planes: planesCopy});
    }

    // Para limpiar todas las especialidades del prestador
    const handlePlanesClear = () => {
        
        // Mantengo los datos del prestador pero sin especialidades
        setPrestadorEspecialidades({...prestadorEspecialidades, planes: []});
    
    }

    // Para actualizar las observaciones del prestador
    const handleChangeLocalObservaciones = (value: string) => {
        setLocalObservaciones(DOMPurify.sanitize(value));
    };
    

    const guardarCambiosLocal = async () => {

        if (idPrestador === 0) {
            // Crear nuevo prestador

            const PrestadorNuevo: PrestadorTabla = {
                id: 0,
                cuit: localCuit,
                razonsocial: localRazonSocial,
                nombrecomercial: localNombreComercial,
                domicilio: localDomicilio,
                telefono: localTelefono,
                whatsapp: localWhatsapp,
                email: localEmail,
                sitioweb: localSitioweb,
                instagram: localInstagram,
                facebook: localFacebook,
                youtube: localYoutube,
                tiktok: localTiktok,
                linkedin: localLinkedin,
                gpslat: localGpslat,
                gpslon: localGpslon,
                idrubro: localIdrubro,
                rubro: localRubro,
                habilitado: localHabilitado,
                fecha_creacion: '',
                fecha_modificacion: '',
                dado_de_baja: 'N',
                fecha_baja: '',
                idpais: localIdPais,
                pais: localPais,
                idprovincia: localIdProvincia,
                provincia: localProvincia,
                idlocalidad: localIdLocalidad,
                localidad: localLocalidad,
                codpostal: localCodPostal,
                logo_big: localLogobig,
                logo_small: localLogosmall,
                observaciones: DOMPurify.sanitize(localObservaciones)
            };            

            const EspecialidadesDelPrestador: EspecialidadSeleccionar[] = prestadorEspecialidades.especialidades;
            const PlanesDelPrestador: PlanSeleccionar[] = prestadorEspecialidades.planes;

            const respuesta = await InsertarPrestador(PrestadorNuevo);

            if (respuesta.prestador_guardado === 'si') {

                // Ahora actualizo las especialidades del prestador                    
                const respuestaEspecialidades = await GuardarEspecialidadesPrestador(respuesta.id_prestador, EspecialidadesDelPrestador);
                const respuestaPlanes = await GuardarPlanesPrestador(respuesta.id_prestador, PlanesDelPrestador);

                if (respuestaEspecialidades.guardar_especialidades_prestador_ok === 'si' && respuestaPlanes.guardar_planes_prestador_ok === 'si') {
                    guardarCambios(PrestadorNuevo);
                } else {
                    alert('Error al guardar los cambios: ' + respuesta.mensaje);
                }
            } else {
                alert('Error al guardar los cambios: ' + respuesta.mensaje);
            }
            
        } else {

            if (eliminando) {
                
                // Se realiza la baja lógica del prestador
                // HABILITADO = N
                // DADO_BAJA = S
                const fechaActual = new Date().toISOString();
                const guardarFecha = fechaActual.slice(0, 10)+" "+fechaActual.slice(11, 19);

                // Modificar datos prestador
                const PrestadorEditado: PrestadorTabla = {
                    id: idPrestador,
                    cuit: localCuit,
                    razonsocial: localRazonSocial,
                    nombrecomercial: localNombreComercial,
                    domicilio: localDomicilio,
                    telefono: localTelefono,
                    whatsapp: localWhatsapp,
                    email: localEmail,
                    sitioweb: localSitioweb,
                    instagram: localInstagram,
                    facebook: localFacebook,
                    youtube: localYoutube,
                    tiktok: localTiktok,
                    linkedin: localLinkedin,
                    gpslat: localGpslat,
                    gpslon: localGpslon,
                    idrubro: localIdrubro,
                    rubro: localRubro,
                    habilitado: 'N',
                    fecha_creacion: localFchCrea,
                    fecha_modificacion: guardarFecha,
                    dado_de_baja: 'S',
                    fecha_baja: guardarFecha,
                    idpais: localIdPais,
                    pais: localPais,
                    idprovincia: localIdProvincia,
                    provincia: localProvincia,
                    idlocalidad: localIdLocalidad,
                    localidad: localLocalidad,
                    codpostal: localCodPostal,
                    logo_big: localLogobig,
                    logo_small: localLogosmall,
                    observaciones: DOMPurify.sanitize(localObservaciones)
                };

                const respuesta = await GuardarPrestador(PrestadorEditado);

                if (respuesta.prestador_guardado === 'si') {
                    guardarCambios(PrestadorEditado);
                } else {
                    alert('Error al guardar los cambios: ' + respuesta.mensaje);
                    console.log("prestador editado: ", PrestadorEditado);
                }
                
            } else {
                
                // Modificar prestador
                const fechaActual = new Date().toISOString();
                const guardarFecha = fechaActual.slice(0, 10)+" "+fechaActual.slice(11, 19);
                
                // Modificar datos prestador
                const PrestadorEditado: PrestadorTabla = {
                    id: idPrestador,
                    cuit: localCuit,
                    razonsocial: localRazonSocial,
                    nombrecomercial: localNombreComercial,
                    domicilio: localDomicilio,
                    telefono: localTelefono,
                    whatsapp: localWhatsapp,
                    email: localEmail,
                    sitioweb: localSitioweb,
                    instagram: localInstagram,
                    facebook: localFacebook,
                    youtube: localYoutube,
                    tiktok: localTiktok,
                    linkedin: localLinkedin,
                    gpslat: localGpslat,
                    gpslon: localGpslon,
                    idrubro: localIdrubro,
                    rubro: localRubro,
                    habilitado: localHabilitado,
                    fecha_creacion: localFchCrea,
                    fecha_modificacion: guardarFecha,
                    dado_de_baja: localDadoBaja,
                    fecha_baja: localDadoBaja === 'S' ? guardarFecha : '',
                    idpais: localIdPais,
                    pais: localPais,
                    idprovincia: localIdProvincia,
                    provincia: localProvincia,
                    idlocalidad: localIdLocalidad,
                    localidad: localLocalidad,
                    codpostal: localCodPostal,
                    logo_big: localLogobig,
                    logo_small: localLogosmall,
                    observaciones: DOMPurify.sanitize(localObservaciones)
                };

                const EspecialidadesDelPrestador: EspecialidadSeleccionar[] = prestadorEspecialidades.especialidades;
                const PlanesDelPrestador: PlanSeleccionar[] = prestadorEspecialidades.planes;

                const respuesta = await GuardarPrestador(PrestadorEditado);

                if (respuesta.prestador_guardado === 'si') {
                        
                    const respuestaEspecialidades = await GuardarEspecialidadesPrestador(idPrestador, EspecialidadesDelPrestador);
                    const respuestaPlanes = await GuardarPlanesPrestador(idPrestador, PlanesDelPrestador);

                    if (respuestaEspecialidades.guardar_especialidades_prestador_ok === 'si' && respuestaPlanes.guardar_planes_prestador_ok === 'si') {
                        guardarCambios(PrestadorEditado);
                    } else {
                        alert('Error al guardar los cambios: ' + respuesta.mensaje);
                    }
                } else {
                    alert('Error al guardar los cambios: ' + respuesta.mensaje);
                }
            }
        }
    };

    return (
        <>
            {isLoading && (
                <div>
                    <p className="text-lg">Por favor espere</p>
                    <CircularProgress />
                </div>
            )}
            {(!isLoading && prestador) && (
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-5">
                    <div className="flex flex-row justify-between items-center space-x-2">
                        <div className="flex flex-col justify-start items-center space-x-2">
                            {eliminando ? (
                                <h1 className="font-metropolis text-xl text-red-700 font-bold mb-2">Eliminando Prestador: {prestador?.razonsocial}</h1>
                            ) : (
                                <h1 className="font-metropolis text-xl font-bold mb-2">{idPrestador > 0 ? "Editando Prestador: " + prestador?.razonsocial : "Nuevo Prestador"}</h1>
                            )}
                        </div> 
                        <div className="flex flex-row justify-center items-center space-x-2 mb-3">
                            {eliminando ? (
                                <>
                                    <button className="vp-btn-rojo-sm" onClick={guardarCambiosLocal} ><DeleteForeverIcon /> Eliminar</button>
                                    <button className="vp-btn-azul-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                                </>
                            ) : (
                                <>
                                    <button className="vp-btn-azul-sm" onClick={guardarCambiosLocal} ><SaveIcon /> Guardar Cambios</button>
                                    <button className="vp-btn-rojo-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                                </>
                            )}                        
                        </div>
                    </div>
                    <div className="border-t-2 border-vp-azul w-full flex flex-row pt-3 flex-wrap justify-between items-start gap-y-3">
                        <div className="flex flex-col justify-start items-center max-w-[120px] flex-grow w-full">
                            <TextField id="cuit" label="Cuit" variant="outlined" value={localCuit} autoFocus
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalCuit}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[380px] flex-grow w-full">
                            <TextField id="razonsocial" label="Razón Social" variant="outlined" value={localRazonSocial}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalRazonSocial}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[380px] flex-grow w-full">
                            <TextField id="nombrecomercial" label="Nombre Comercial" variant="outlined" value={localNombreComercial}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalNombreComercial}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[380px] flex-grow w-full">
                            <TextField id="domicilio" label="Domicilio" variant="outlined" value={localDomicilio}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalDomicilio}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[220px] flex-grow w-full">
                            <TextField id="telefono" label="Teléfono" variant="outlined" value={localTelefono}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalTelefono}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[120px] flex-grow w-full">
                            <TextField id="whatsapp" label="Whatsapp" variant="outlined" value={localWhatsapp}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalWhatsapp}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[380px] flex-grow w-full">
                            <TextField id="email" label="Email" variant="outlined" value={localEmail}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalEmail}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[380px] flex-grow w-full">
                            <TextField id="sitioweb" label="Sitio Web" variant="outlined" value={localSitioweb}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalSitioweb}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[380px] flex-grow w-full">
                            <TextField id="instagram" label="Instagram" variant="outlined" value={localInstagram}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalInstagram}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[380px] flex-grow w-full">
                            <TextField id="facebook" label="Facebook" variant="outlined" value={localFacebook}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalFacebook}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[380px] flex-grow w-full">
                            <TextField id="youtube" label="Youtube" variant="outlined" value={localYoutube}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalYoutube}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[380px] flex-grow w-full">
                            <TextField id="tiktok" label="Tiktok" variant="outlined" value={localTiktok}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalTiktok}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[380px] flex-grow w-full">
                            <TextField id="linkedin" label="LinkedIn" variant="outlined" value={localLinkedin}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalLinkedin}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[200px] flex-grow w-full">
                            <TextField id="gpslat" label="GPS Lat" variant="outlined" value={localGpslat}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalGpslat}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[200px] flex-grow w-full">
                            <TextField id="gpslon" label="GPS Lon" variant="outlined" value={localGpslon}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalGpslon}
                                disabled={eliminando}
                            />
                        </div>                        
                        {prestador.id > 0 && (
                            <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                <TextField id="fchcrea" label="Fec. Creado" variant="outlined" value={localFchCrea}
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        '& .MuiInputLabel-outlined': {
                                            top: '-6px',
                                        },
                                        '& .MuiInputLabel-shrink': {
                                            top: '0px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    disabled
                                />
                            </div>)
                        }
                        <div className="flex flex-col justify-between items-center max-w-[320px] flex-grow w-full">
                            <SeleccionarRubro rubroInicial={localIdrubro} onChange={handleSeleccionarRubro} onClear={handleClearSeleccionarRubro} eliminando={eliminando}/>   
                        </div>
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-habilitado"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Habilitado
                                    </InputLabel>
                                <Select
                                    labelId="label-habilitado"
                                    id="habilitado"
                                    value={localHabilitado}
                                    label="Habilitado"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalHabitado}
                                    disabled={eliminando}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-dadobaja"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Baja
                                    </InputLabel>
                                <Select
                                    labelId="label-dadobaja"
                                    id="dadobaja"
                                    value={localDadoBaja}
                                    label="Baja"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalDadoBaja}
                                    disabled={eliminando}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>
                        {localDadoBaja === 'S' && (
                            <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                <TextField id="fchbaja" label="Fec. Baja" variant="outlined" value={localFchBaja}
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        '& .MuiInputLabel-outlined': {
                                            top: '-6px',
                                        },
                                        '& .MuiInputLabel-shrink': {
                                            top: '0px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    disabled
                                />
                            </div>)
                        }
                        <div className="flex flex-col justify-between items-center flex-grow w-full mb-2">
                            <SeleccionarPais paisInicial={localIdPais} onChange={handlePaisChange} onClear={handlePaisClear} eliminando={eliminando}/>
                        </div>
                        <div className="flex flex-col justify-between items-center flex-grow w-full mb-2">
                            <SeleccionarProvincia paisInicial={localIdPais} provinciaInicial={localIdProvincia} onChange={handleProvinciaChange} onClear={handleProvinciaClear} eliminando={eliminando}/>
                        </div>
                        <div className="flex flex-col justify-between items-center flex-grow w-full mb-2">
                            <SeleccionarLocalidad paisInicial={localIdPais} provinciaInicial={localIdProvincia} localidadInicial={localIdLocalidad} onChange={handleLocalidadChange} onClear={handleLocalidadClear} eliminando={eliminando}/>
                        </div>
                        <div className="flex flex-col justify-between items-center flex-grow w-full pb-4">
                            <SeleccionarEspecialidades elPrestador={prestadorEspecialidades} onChange={handleEspecialidadesChange} onRemove={handleEspecialidadesRemove} onClear={handleEspecialidadesClear} eliminando={eliminando}/>   
                        </div>
                        <div className="flex flex-col justify-between items-center  flex-grow w-full pb-1">
                            <SeleccionarPlanes elPrestador={prestadorEspecialidades} onChange={handlePlanesChange} onRemove={handlePlanesRemove} onClear={handlePlanesClear} eliminando={eliminando}/>   
                        </div>
                        <div className="flex flex-col justify-start items-start flex-grow w-full min-h-full border-b-2 border-vp-azul mb-5">
                            <label className="font-bold">Observaciones</label>
                            <ReactQuill
                                theme="snow"
                                value={localObservaciones}
                                onChange={handleChangeLocalObservaciones}
                                placeholder="Ingresar observaciones aquí..."
                                style={{width: "100%", height: '200px', scrollbarWidth: 'thin', marginBottom: '60px',}}
                                readOnly={eliminando}
                            />                                
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
